import bugsnag from "@bugsnag/js";
import { createBrowserHistory } from "history";
import Lottie from "lottie-react";
import { FunctionComponent, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { Faq } from "@Components/molecules/category/Faq";
import { LargeProductCards } from "@Components/molecules/category/LargeProductCards";
import { SmallColoredIcons } from "@Components/molecules/category/SmallColoredIcons";
import { SeoDescription } from "@Components/molecules/home/SeoDescription";
import WithLocale from "@Components/WithLocale";
import useBannerKountriesHeight from "@Hooks/banner/useBannerKountriesHeight";
import { useBreakpoint } from "@Hooks/useBreakpoint";
import CheerzApiProvider from "@Providers/CheerzApiProvider";
import { category as categoryMock } from "@Samples/mocks/category";
import { Page, Section, Tile } from "@Types/pagify";
import { SettingsContent } from "@Types/settings";
import UserContent from "@Types/user";
import { getCsrfToken } from "@Utils/meta";
import { convertSettingsFromApiToContent } from "@Utils/settings";
import { setDataLayerMandatoryPageTrackers } from "@Utils/track";
import { convertFromApiToUserContent } from "@Utils/user";
import animation from "./animation.json";
import "./style.scss";

const history = createBrowserHistory();

interface Props {
  category: Page;
}

interface ContainerProps {
  categoryTag: string;
}

interface ComponentsMapping {
  [key: string]: FunctionComponent<{ section: Section; tiles: Tile[] }>;
}

export const Category: FunctionComponent<Props> = ({ category }) => {
  const categoryComponentsList: ComponentsMapping = {
    faq_web: Faq,
    large_product_cards_web: LargeProductCards,
    seo_description_web: SeoDescription,
    small_colored_icons_web: SmallColoredIcons,
  };

  const CategoryComponents = category.sections.map((section: Section) => {
    const Component = categoryComponentsList[section.displayType];
    const tiles = section.tileSlugs
      .map((slug) => category.tiles.find((tile) => tile.slug === slug))
      .filter((tile): tile is Tile => !!tile);

    return Component ? (
      <Component key={section.displayType} section={section} tiles={tiles} />
    ) : null;
  });

  const { isLargeScreen } = useBreakpoint();
  const headerHeight = isLargeScreen ? 118 : 56;
  const bannerKountriesHeight = useBannerKountriesHeight();

  const pageTitle = category.header.text;

  return (
    <div className="Category" style={{ marginTop: headerHeight + bannerKountriesHeight }}>
      {!!pageTitle && <div className="Category-title">{pageTitle}</div>}
      {CategoryComponents}
    </div>
  );
};

const CategoryContainer: FunctionComponent<ContainerProps> = ({ categoryTag }) => {
  const [category, setCategory] = useState<Page>();
  const [settings, setSettings] = useState<SettingsContent>();
  const [user, setUser] = useState<UserContent>();
  const [loading, setLoading] = useState<boolean>();
  const intl = useIntl();
  const locale = intl.locale;

  const cheerzApiProvider = new CheerzApiProvider(locale, getCsrfToken(), true);

  useEffect(() => {
    setLoading(true);
    cheerzApiProvider
      .getUser()
      .then((userData) => {
        setUser(convertFromApiToUserContent(userData));
        return cheerzApiProvider.getSettings(locale);
      })
      .then((settingsData) => {
        setSettings(convertSettingsFromApiToContent(settingsData.response));
        return cheerzApiProvider.getPage(categoryTag, settingsData.response).then(() => {
          // TODO: Remove mock to get real data
          setTimeout(() => {
            setCategory(categoryMock);
            setLoading(false);
          }, 1000);
        });
      })
      .catch((error: Error) => {
        bugsnag.notify(error);
        // TODO: Remove mock to get real data
        setTimeout(() => {
          setCategory(categoryMock);
          setLoading(false);
        }, 1000);
      });
  }, []);

  useEffect(() => {
    if (category && settings && user) {
      setDataLayerMandatoryPageTrackers(
        settings?.currency,
        settings?.lang,
        categoryTag,
        "Category",
        window.location.pathname,
        user
      );
    }
  }, [category, settings, user]);

  return (
    <WithLocale history={history}>
      {loading && (
        <div className="Category-loader">
          <Lottie
            style={{ height: "50px" }}
            animationData={animation}
            autoplay={true}
            loop={true}
          />
        </div>
      )}
      {category && <Category category={category} />}
    </WithLocale>
  );
};

export default CategoryContainer;
